import {
  AuthMeResponseSchema,
  TwoFactorBackupCodesResponseSchema,
  TwoFactorSetupResponseSchema,
  TwoFactorStatusResponseSchema,
  UpdateUserSettingsResponseSchema
} from './schema';
import type {
  ChangePasswordParams,
  DisableTwoFactorParams,
  EnableTwoFactorParams,
  UpdateUserSettingsParams
} from './schema';
import { HTTP_METHOD } from '../../consts';
import { NoContentResponseSchema } from '../../schemas';
import { serializeRequestBody } from '../../serializer';
import type { Transport, ApiCallOptions } from '../../types';

export const createUserApi = (transport: Transport) => ({
  authMe: (options?: ApiCallOptions) =>
    transport('manage/v1/auth/me', {
      method: HTTP_METHOD.GET,
      deserializer: AuthMeResponseSchema,
      signal: options?.signal
    }),
  updateUser: (data: UpdateUserSettingsParams, options?: ApiCallOptions) =>
    transport('manage/v1/user/settings', {
      method: HTTP_METHOD.PATCH,
      body: serializeRequestBody(data),
      deserializer: UpdateUserSettingsResponseSchema,
      signal: options?.signal
    }),
  /**
   * Change the signed-in user's own password. The API signs out the user's other sessions and
   * keeps the one that sent the request, which it identifies by its refresh-token cookie.
   */
  changePassword: (params: ChangePasswordParams, options?: ApiCallOptions) =>
    transport('manage/v1/user/password', {
      method: HTTP_METHOD.PUT,
      body: serializeRequestBody(params),
      deserializer: NoContentResponseSchema,
      signal: options?.signal
    }),
  /** The signed-in user's two-factor authentication state. */
  getTwoFactorStatus: (options?: ApiCallOptions) =>
    transport('manage/v1/user/2fa', {
      method: HTTP_METHOD.GET,
      deserializer: TwoFactorStatusResponseSchema,
      signal: options?.signal
    }),
  /** Starts or resumes 2FA set-up and returns the link to show as a QR code. */
  setupTwoFactor: (options?: ApiCallOptions) =>
    transport('manage/v1/user/2fa/setup', {
      method: HTTP_METHOD.POST,
      deserializer: TwoFactorSetupResponseSchema,
      signal: options?.signal
    }),
  /** Turns 2FA on once the user confirms a code from their authenticator app, and returns the backup codes. */
  enableTwoFactor: (params: EnableTwoFactorParams, options?: ApiCallOptions) =>
    transport('manage/v1/user/2fa/actions/enable', {
      method: HTTP_METHOD.POST,
      body: serializeRequestBody(params),
      deserializer: TwoFactorBackupCodesResponseSchema,
      signal: options?.signal
    }),
  /** Turns 2FA off once the user confirms an authenticator code or an unused backup code. */
  disableTwoFactor: (params: DisableTwoFactorParams, options?: ApiCallOptions) =>
    transport('manage/v1/user/2fa/actions/disable', {
      method: HTTP_METHOD.POST,
      body: serializeRequestBody(params),
      deserializer: NoContentResponseSchema,
      signal: options?.signal
    })
});

export type UserApi = ReturnType<typeof createUserApi>;
